import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../../Components/Games/TopBar.jsx';
import './MemoryBlitz.css';
import ResultPage from '../../Components/Games/ResultPage.jsx';
import StartGame from '../../Components/Games/StartGame.jsx';
import overlay from '../../assets/img/overlay.png';
import GameIntroScreen from '../../Components/Games/GameIntroScreen.jsx';
import memoryBlitzIcon from '../../assets/img/icon-Memory-Blitz.png';
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

// --- Assets (Emojis) ---
const ITEMS = ['⚡', '💎', '🔥', '🚀', '⭐', '❤️', '🍀', '🎵', '🍄', '🔔', '👑', '🎲'];

// --- Colors for Filled Slots ---
const SLOT_COLORS = ['#22c55e', '#ef4444', '#3b82f6', '#a855f7', '#f59e0b', '#ec4899'];

// --- Helper Functions ---
const generateSequence = (level) => {
    const MAX_ITEMS = 9;
    const length = Math.min(2 + level, MAX_ITEMS);
    const shuffled = [...ITEMS].sort(() => Math.random() - 0.5);
    const chosen = shuffled.slice(0, length);
    const sequence = chosen.map((icon) => ({
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        icon
    }));
    return sequence;
};

const shuffleArray = (array) => {
    return [...array].sort(() => Math.random() - 0.5);
};

// --- Sub-Component: Tutorial ---
// --- Sub-Component: Interactive Tutorial ---
const TutorialContent = ({ onComplete }) => {

    const tutorialSequence = [
        { id: 't1', icon: '⚡' },
        { id: 't2', icon: '🚀' }
    ];

    const [phase, setPhase] = useState("MEMORIZE"); // MEMORIZE -> RECALL
    const [userSeq, setUserSeq] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [options, setOptions] = useState([]);


    useEffect(() => {
    const t = setTimeout(() => {
        setPhase("RECALL");

        let shuffled = shuffleArray(tutorialSequence);

        // force different order from memorize
        if (
            shuffled.length === tutorialSequence.length &&
            shuffled.every((v, i) => v.id === tutorialSequence[i].id)
        ) {
            shuffled = [...shuffled].reverse();
        }

        setOptions(shuffled);

    }, 3000);

    return () => clearTimeout(t);
}, []);

    const handlePick = (item) => {
        if (phase !== "RECALL") return;
        if (feedback === "wrong") return;

        const targetIndex = userSeq.length;
        const targetItem = tutorialSequence[targetIndex];

        if (item.id === targetItem.id) {

            const next = [...userSeq, item];
            setUserSeq(next);

            if (next.length === tutorialSequence.length) {
                setTimeout(onComplete, 600);
            }

        } else {

            setFeedback("wrong");

            setTimeout(() => {
                setFeedback(null);
            }, 500);
        }
    };

    return (
        <div className="tutorial-board-sreen">

            <button
                className="skip-btn-sreen"
                onClick={onComplete}
            >
                Skip ⏩
            </button>

            <h2 className="tutorial-header-sreen">
                {phase === "MEMORIZE" ? "Watch carefully…" : "Now tap in order"}
            </h2>

            <div className="slots-container-sreen">
                {tutorialSequence.map((item, index) => {

                    const isFilled = phase === "MEMORIZE" || index < userSeq.length;
                    const isCurrentTarget = index === userSeq.length;
                    const isError = feedback === "wrong" && isCurrentTarget;

                    let bgColor = "transparent";
                    let borderStyle = "2px dashed #475569";
                    let animation = "none";

                    if (isFilled) {
                        bgColor = "#bed8c8";
                        borderStyle = "none";
                        animation = "popIn 0.3s ease forwards";
                    } else if (isError) {
                        bgColor = "rgba(239,68,68,0.2)";
                        borderStyle = "2px solid #ef4444";
                        animation = "shake 0.4s cubic-bezier(.36,.07,.19,.97) both";
                    }

                    const content =
                        phase === "MEMORIZE"
                            ? item
                            : userSeq[index];

                    return (
                        <div
                            key={item.id}
                            className="slot-card-sreen"
                            style={{
                                backgroundColor: bgColor,
                                border: borderStyle,
                                animation
                            }}
                        >
                            {isFilled && (
                                <span className="emoji-sreen">
                                    {content?.icon}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {phase === "RECALL" && (
                <div className="options-container-sreen">
                    {options.map(item => {
                        const used = userSeq.find(i => i.id === item.id);
                        return (
                            <button
                                key={item.id}
                                className="option-btn-sreen"
                                disabled={used}
                                onClick={() => handlePick(item)}
                                style={{ opacity: used ? 0 : 1 }}
                            >
                                <span className="emoji-sreen">{item.icon}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <p className="tutorial-subtext-sreen" style={{ marginTop: 14 }}>
                This is exactly how the real game works.
            </p>

        </div>
    );
};

// --- Main Component ---
const MemoryBlitz2 = () => {
    useGameBackNavigation();
    const [screen, setScreen] = useState("intro");
    const [gameState, setGameState] = useState('START'); // START -> TUTORIAL -> MEMORIZE
    const [level, setLevel] = useState(1);
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [sequence, setSequence] = useState([]);
    const [userSequence, setUserSequence] = useState([]);
    const [jumbledOptions, setJumbledOptions] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [madeMistake, setMadeMistake] = useState(false);
    const [memorizeProgress, setMemorizeProgress] = useState(100);

    // Statistics
    const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem('bestScore')) || 0);
    const [correct, setCorrect] = useState(0);
    const [wrong, setWrong] = useState(0);
    const [currentStreak, setCurrentStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(() => parseInt(localStorage.getItem('bestStreak')) || 0);
    const [fastResponses, setFastResponses] = useState(0);
    const [totalLevelsCompleted, setTotalLevelsCompleted] = useState(0);
    const [goqiiBest, setGoqiiBest] = useState(0);
    const timerRef = useRef(null);
    const gameStartTimeRef = useRef(null);
    const [cid, setCid] = useState("");
    const [groupName, setGroupName] = useState("");
    const [gameStateApi, setGameStateApi] = useState("initial");
    const [lastInsertedId, setLastInsertedId] = useState(null);
    const levelStartTimeRef = useRef(null);
    const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
    const [backendSkill, setBackendSkill] = useState("");
    const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
    const [configError, setConfigError] = useState(false);
    const [tapStreak, setTapStreak] = useState(0);




    // --- Game Logic ---
    const startGame = async () => {
        const { encryptedId, gid } = getUrlParams();

        try {
            const res = await lifestyleApi.initialGameScore(encryptedId, gid);

            if (res.data?.code === 200) {
                const data = res.data.data;

                setCid(data.cid);
                setGroupName(data.groupName);
                setLastInsertedId(data.lastInsertedId);
                setGameStateApi("initial");
            }
        } catch (err) {
            console.error("initialGameScore failed", err);
        }

        setScore(0);
        setLevel(1);
        setTimeLeft(60);
        setCorrect(0);
        setWrong(0);
        setCurrentStreak(0);
        setTapStreak(0);
        setFastResponses(0);
        setTotalLevelsCompleted(0);
        // Clear previous game data immediately so background is empty
        setSequence([]);
        setUserSequence([]);
        setJumbledOptions([]);
        setFeedback(null);
        setScreen("game");
        setGameState('READY');

        setTimeout(() => {
            setGameState('MEMORIZE');
            startLevel(1);
            startTimer();
            gameStartTimeRef.current = Date.now();
        }, 1200);

    };


    const fetchGameConfig = () => {
        setConfigError(false);
        const { encryptedId, gid } = getUrlParams();

        lifestyleApi.fetchGameDetails(encryptedId, gid)
            .then(res => {
                if (res.data?.code === 200) {
                    const d = res.data.data;
                    setBestScore(d.bestScore);
                    setGoqiiBest(d.goqiiBest);
                    setWeightages({
                        attemptWeightage: d.attemptWeightage ?? 1,
                        accuracyWeightage: d.accuracyWeightage ?? 1,
                        streakWeightage: d.streakWeightage ?? 1,
                        fastWeightage: d.fastWeightage ?? 1,
                        scoreWeightage: d.scoreWeightage ?? 1,
                        baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
                        correctMinQuestion: d.correctMinQuestion || 1,
                        streakMinQuestion: d.streakMinQuestion || 1,
                        fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
                        scorePoints: d.scorePoints || 1,
                    });
                } else {
                    setConfigError(true);
                }
            })
            .catch(err => {
                console.error("fetchGameDetails failed", err);
                setConfigError(true);
            });
    };

    useEffect(() => {
        fetchGameConfig();
    }, []);




    const startTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    setGameState('GAMEOVER');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const startLevel = (currentLevel) => {
        setUserSequence([]);
        setFeedback(null);
        setMadeMistake(false);
        const newSeq = generateSequence(currentLevel);
        setSequence(newSeq);
        setGameState('MEMORIZE');
        const memorizeTime = Math.max(2000, newSeq.length * 1000);
        let progress = 100;
        setMemorizeProgress(100);
        const interval = setInterval(() => {
            progress -= 100 / (memorizeTime / 100);
            setMemorizeProgress(progress);
            if (progress <= 0) clearInterval(interval);
        }, 100);
        setTimeout(() => {
            setGameState(prev => prev === 'GAMEOVER' ? 'GAMEOVER' : 'RECALL');
            setJumbledOptions(shuffleArray(newSeq));
            levelStartTimeRef.current = Date.now();
        }, memorizeTime);
    };

    const handleItemClick = (item) => {
        if (gameState !== 'RECALL') return;
        if (feedback === 'wrong') return;
        const targetIndex = userSequence.length;
        const targetItem = sequence[targetIndex];
        if (item.id === targetItem.id) {
            setCorrect(prev => prev + 1);
            const newUserSeq = [...userSequence, item];
            setUserSequence(newUserSeq);

            const isLastTap = newUserSeq.length === sequence.length;
            let fastBonus = 0;
            if (isLastTap && levelStartTimeRef.current) {
                const levelTimeTaken = (Date.now() - levelStartTimeRef.current) / 1000;
                if (levelTimeTaken < sequence.length * 1.1) {
                    fastBonus = weightages.fastWeightage * 10;
                }
            }

            // tapStreak here is the consecutive-correct-tap count BEFORE this tap
            const streakBonus = weightages.streakWeightage * 10 * tapStreak;
            const gained = weightages.scorePoints + streakBonus + fastBonus;
            setScore(prev => {
                const newScore = Math.max(0, prev + gained);
                if (newScore > bestScore) {
                    setBestScore(newScore);
                    localStorage.setItem('bestScore', newScore.toString());
                }
                return newScore;
            });
            setTapStreak(prev => prev + 1);

            if (isLastTap) {
                handleLevelComplete();
            }
        } else {
            setWrong(prev => prev + 1);
            handleMistake();
        }
    };

    const handleLevelComplete = () => {
        // setCorrect(prev => prev + 1);
        setCurrentStreak(prev => {
            const newStreak = prev + 1;
            if (newStreak > bestStreak) {
                setBestStreak(newStreak);
                localStorage.setItem('bestStreak', newStreak.toString());
            }
            return newStreak;
        });
        setTotalLevelsCompleted(prev => prev + 1);

        const levelTimeTaken = (Date.now() - levelStartTimeRef.current) / 1000;
        if (levelTimeTaken < sequence.length * 1.1) {
            setFastResponses(prev => prev + 1);
            setFeedback("fast");
        } else {
            setFeedback("level-up");
        }

        setTimeout(() => {
            const nextLevel = level + 1;
            setLevel(nextLevel);
            startLevel(nextLevel);
        }, 1000);

    };

    const handleMistake = () => {
        setFeedback('wrong');
        setMadeMistake(true);
        // setWrong(prev => prev + 1);
        setCurrentStreak(0);
        setTapStreak(0);
        setScore(s => Math.max(0, s - weightages.scorePoints / 2));
        setTimeout(() => setFeedback(null), 500);
    };

    const calculateAccuracy = () => {
        const totalAttempts = correct + wrong;
        return totalAttempts === 0 ? 0 : Math.round((correct / totalAttempts) * 100);
    };

    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    const saveFinalGameScore = async () => {
        const { encryptedId, gid } = getUrlParams();
        const goqiiUserId = encryptedId;
        const totalQuestions = correct + wrong;

        const payload = {
            goqiiUserId,
            gid,
            totalQuestions,
            correctAnswers: correct,
            wrongAnswers: wrong,
            longestStreak: bestStreak,
            fastResponse: fastResponses,
            score,
            cid,
            lastInsertedId,
        };

        try {
            const res = await lifestyleApi.saveGameScore(
                payload.goqiiUserId,
                payload.gid,
                payload.totalQuestions,
                payload.correctAnswers,
                payload.wrongAnswers,
                payload.longestStreak,
                payload.fastResponse,
                payload.score,
                payload.cid,
                payload.lastInsertedId
            );

            if (res?.data?.data?.cognitiveScore !== undefined) {
                setBackendCognitiveScore(res.data.data.cognitiveScore);
            }
            if (res?.data?.data?.skill) {
                setBackendSkill(res.data.data.skill);
            }
        } catch (err) {
            console.error("saveGameScore failed", err);
        }
    };

    //  AUTO SUBMIT WHEN GAME OVER
    useEffect(() => {
        if (gameState === "GAMEOVER") {
            saveFinalGameScore();
        }
    }, [gameState]);

    const getQuote = () => {
        if (backendCognitiveScore >= 90) return "Lightning-fast memory! Your mind holds patterns effortlessly.";
        if (backendCognitiveScore >= 75) return "Great recall! Your working memory is strong.";
        if (backendCognitiveScore >= 60) return "Good focus. Your memory is improving.";
        if (backendCognitiveScore >= 40) return "Nice effort. Keep training your recall power.";
        return "Every round strengthens your memory. Try again!";
    };


    const renderSequenceSlots = () => {
        return sequence.map((item, index) => {
            const isFilled = gameState === 'MEMORIZE' || index < userSequence.length;
            const content = gameState === 'MEMORIZE' ? item : userSequence[index];
            const isCurrentTarget = index === userSequence.length;
            const isError = feedback === 'wrong' && isCurrentTarget;

            let bgColor = 'transparent';
            let borderStyle = '2px dashed #475569';
            let animation = 'none';
            let boxShadow = 'none';

            if (isFilled) {
                bgColor = SLOT_COLORS[index % SLOT_COLORS.length];
                borderStyle = 'none';
                boxShadow = `0 0 210px ${bgColor}50`;
                animation = 'popIn 0.3s ease forwards';
            } else if (isError) {
                bgColor = 'rgba(239, 68, 68, 0.2)';
                borderStyle = '2px solid #ef4444';
                boxShadow = '0 0 15px rgba(239, 68, 68, 0.5)';
                animation = 'shake 0.4s cubic-bezier(.36,.07,.19,.97) both';
            }

            return (
                <div key={index} className="slot-card-sreen" style={{ backgroundColor: bgColor, border: borderStyle, boxShadow: boxShadow, animation: animation }}>
                    {isFilled && <span className="emoji-sreen">{content.icon}</span>}
                </div>
            );
        });
    };

    return (
        <div className="app-container-sreen memory-blitz-wrapper">
            {configError && (
                <div className="api-error-overlay">
                    <div className="api-error-card">
                        <div className="api-error-title">Something went wrong</div>
                        <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
                        <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
                    </div>
                </div>
            )}

            {screen === "game" && gameState !== 'TUTORIAL' && gameState !== 'READY' && (
                <TopBar score={score} timeLeft={timeLeft} changeDurationMs={700} timerAlertThreshold={10} />
            )}

            <div className="main-area-sreen">
                {screen === "intro" ? (
                    <GameIntroScreen
                        backgroundImage={overlay} gameLogo={memoryBlitzIcon} title="Memory Blitz"
                        onStart={() => setScreen("start")}
                    />
                ) : gameState === 'START' ? (
                    <StartGame
                        backgroundImage={overlay}
                        //  hbTitle="Health Benefits"
                        hbHeading="Working Memory"
                        hbDescription="Memorize the sequence and recall it in the correct order to strengthen your working memory."
                        yourBest={bestScore}
                        goqiiBest={goqiiBest}
                        onStart={() => { setScreen("game"); setGameState('TUTORIAL'); }}
                    />
                ) : gameState === 'GAMEOVER' ? (
                    <ResultPage
                        visible={true} score={score} best={bestScore} correct={correct} wrong={wrong}
                        longestStreak={bestStreak} fastResponses={fastResponses}
                        cognitiveScore={backendCognitiveScore} domainLabel={backendSkill}
                        quote={getQuote()} onReplay={startGame}
                    />
                ) : (
                    <div className={`game-board-sreen ${gameState === 'TUTORIAL' ? 'tutorial-mode' : ''}`}>
                        {/* --- NEW: THE FOCUS-HOLD STYLE START OVERLAY --- */}
                        {gameState === 'READY' && (
                            <div className="ready-overlay-sreen">
                                <div className="ready-text-sreen">START!</div>
                            </div>
                        )}
                        {gameState === 'TUTORIAL' ? (
                            <TutorialContent onComplete={startGame} />
                        ) : (
                            <>
                                {(feedback === 'level-up' || feedback === 'fast') && (
                                    <div className="overlay-success-sreen">
                                        {feedback === 'fast' ? 'FAST & CORRECT!' : 'CORRECT!'}
                                    </div>
                                )}

                                {gameState === "MEMORIZE" && (
                                    <div className="memorize-bar-wrapper-sreen">
                                        <div className="memorize-bar-sreen" style={{ width: memorizeProgress + "%" }} />
                                    </div>
                                )}
                                <h2 className="phase-text-sreen">
                                    {gameState === 'MEMORIZE' ? 'MEMORIZE SEQUENCE' : 'RECALL SEQUENCE'}
                                </h2>

                                <div className="slots-container-sreen">{renderSequenceSlots()}</div>

                                <div className="options-container-sreen">
                                    {gameState === 'RECALL' && jumbledOptions.map((item) => {
                                        const isSelected = userSequence.find(u => u.id === item.id);
                                        return (
                                            <button
                                                key={item.id} onClick={() => handleItemClick(item)} disabled={isSelected}
                                                className="option-btn-sreen"
                                                style={{ opacity: isSelected ? 0 : 1, pointerEvents: isSelected ? 'none' : 'auto', transform: isSelected ? 'scale(0.8)' : 'scale(1)' }}
                                            >
                                                <span className="emoji-sreen">{item.icon}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemoryBlitz2;